// Google Analytics Event Tracking for ZoningPal
// This utility helps track key user actions and conversions

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export interface GAEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

export interface GAPageView {
  page_title?: string;
  page_location?: string;
  page_path?: string;
}

class GoogleAnalytics {
  private measurementId: string = 'G-VHXT2WDNJR';

  constructor() {
    // Ensure gtag is available
    if (typeof window !== 'undefined' && !window.gtag) {
      console.warn('Google Analytics gtag not loaded');
    }
  }

  /**
   * Track a custom event
   */
  trackEvent(event: GAEvent): void {
    if (typeof window === 'undefined' || !window.gtag) {
      console.warn('Google Analytics not available');
      return;
    }

    const eventData: any = {
      event_category: event.category,
      event_label: event.label,
      value: event.value,
    };

    // Add custom parameters if provided
    if (event.custom_parameters) {
      Object.assign(eventData, event.custom_parameters);
    }

    window.gtag('event', event.action, eventData);
    
    console.log('📊 GA Event:', event.action, eventData);
  }

  /**
   * Track page view
   */
  trackPageView(pageData: GAPageView = {}): void {
    if (typeof window === 'undefined' || !window.gtag) {
      console.warn('Google Analytics not available');
      return;
    }

    window.gtag('config', this.measurementId, {
      page_title: pageData.page_title || document.title,
      page_location: pageData.page_location || window.location.href,
      page_path: pageData.page_path || window.location.pathname,
    });

    console.log('📊 GA Page View:', pageData.page_path || window.location.pathname);
  }

  /**
   * Track conversion (purchase)
   */
  trackConversion(transactionId: string, value: number, currency: string = 'CAD'): void {
    if (typeof window === 'undefined' || !window.gtag) {
      console.warn('Google Analytics not available');
      return;
    }

    // Track purchase event for GA4
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value: value,
      currency: currency,
    });

    // Track conversion for Google Ads (AW-17672737552)
    // Conversion label: BwMlCO2JqbwbEJCmgutB
    window.gtag('event', 'conversion', {
      'send_to': 'AW-17672737552/BwMlCO2JqbwbEJCmgutB',
      'value': value,
      'currency': currency,
      'transaction_id': transactionId
    });

    console.log('💰 GA Conversion:', transactionId, value, currency);
    console.log('💰 Google Ads Conversion:', transactionId, value, currency);
  }

  /**
   * Track user engagement
   */
  trackEngagement(action: string, engagementTime: number): void {
    this.trackEvent({
      action,
      category: 'engagement',
      value: Math.round(engagementTime),
      custom_parameters: {
        engagement_time_msec: engagementTime,
      },
    });
  }
}

// Create singleton instance
export const ga = new GoogleAnalytics();

// ================================================================
// ZONINGPAL SPECIFIC EVENT TRACKING FUNCTIONS
// ================================================================

/**
 * Track map launches
 */
export const trackMapLaunch = (source: string = 'homepage') => {
  ga.trackEvent({
    action: 'launch_map',
    category: 'navigation',
    label: source,
    custom_parameters: {
      source_page: source,
    },
  });
};

/**
 * Track report downloads
 */
export const trackReportDownload = (reportType: string = 'sample') => {
  ga.trackEvent({
    action: 'download_report',
    category: 'content',
    label: reportType,
    custom_parameters: {
      report_type: reportType,
    },
  });
};

/**
 * Track user authentication
 */
export const trackAuth = (action: 'signup' | 'login' | 'logout') => {
  ga.trackEvent({
    action,
    category: 'authentication',
    custom_parameters: {
      auth_method: 'email', // or 'google', 'facebook', etc.
    },
  });
};

/**
 * Track property searches
 */
export const trackPropertySearch = (address: string, coordinates?: { lat: number; lng: number }) => {
  ga.trackEvent({
    action: 'search_property',
    category: 'search',
    label: address,
    custom_parameters: {
      search_address: address,
      has_coordinates: !!coordinates,
      latitude: coordinates?.lat,
      longitude: coordinates?.lng,
    },
  });
};

/**
 * Track analysis requests
 */
export const trackAnalysisRequest = (address: string, hasCredits: boolean) => {
  ga.trackEvent({
    action: 'request_analysis',
    category: 'analysis',
    label: address,
    custom_parameters: {
      search_address: address,
      user_has_credits: hasCredits,
    },
  });
};

/**
 * Track purchase initiation
 */
export const trackPurchaseInitiated = (productType: string, price: number) => {
  ga.trackEvent({
    action: 'begin_checkout',
    category: 'ecommerce',
    label: productType,
    value: price,
    custom_parameters: {
      product_type: productType,
      currency: 'CAD',
    },
  });
};

/**
 * Track successful purchase
 */
export const trackPurchaseCompleted = (transactionId: string, productType: string, price: number, credits: number) => {
  // Track as conversion
  ga.trackConversion(transactionId, price, 'CAD');
  
  // Track as custom event
  ga.trackEvent({
    action: 'purchase_completed',
    category: 'ecommerce',
    label: productType,
    value: price,
    custom_parameters: {
      transaction_id: transactionId,
      product_type: productType,
      credits_purchased: credits,
      currency: 'CAD',
    },
  });
};

/**
 * Track credit usage
 */
export const trackCreditUsage = (reportId: string, address: string) => {
  ga.trackEvent({
    action: 'use_credit',
    category: 'usage',
    label: reportId,
    custom_parameters: {
      report_id: reportId,
      property_address: address,
    },
  });
};

/**
 * Track pricing page views
 */
export const trackPricingView = (source: string = 'navigation') => {
  ga.trackEvent({
    action: 'view_pricing',
    category: 'navigation',
    label: source,
    custom_parameters: {
      source_page: source,
    },
  });
};

/**
 * Track contact form submissions
 */
export const trackContactForm = (formType: string = 'general') => {
  ga.trackEvent({
    action: 'submit_contact_form',
    category: 'engagement',
    label: formType,
    custom_parameters: {
      form_type: formType,
    },
  });
};

/**
 * Track enterprise inquiries
 */
export const trackEnterpriseInquiry = (source: string = 'pricing_page') => {
  ga.trackEvent({
    action: 'enterprise_inquiry',
    category: 'lead_generation',
    label: source,
    custom_parameters: {
      source_page: source,
      lead_type: 'enterprise',
    },
  });
};

/**
 * Track feature usage
 */
export const trackFeatureUsage = (feature: string, action: string) => {
  ga.trackEvent({
    action: `use_${action}`,
    category: 'feature_usage',
    label: feature,
    custom_parameters: {
      feature_name: feature,
      feature_action: action,
    },
  });
};

/**
 * Track blog article views
 */
export const trackBlogArticleView = (articleTitle: string, category: string) => {
  ga.trackEvent({
    action: 'blog_article_view',
    category: 'blog_engagement',
    label: articleTitle,
    custom_parameters: {
      article_title: articleTitle,
      article_category: category,
      page_type: 'blog_article',
    },
  });
};

/**
 * Track blog category views
 */
export const trackBlogCategoryView = (category: string) => {
  ga.trackEvent({
    action: 'blog_category_view',
    category: 'blog_engagement',
    label: category,
    custom_parameters: {
      category_name: category,
      page_type: 'blog_category',
    },
  });
};

/**
 * Track blog interactions
 */
export const trackBlogInteraction = (action: string, articleTitle?: string) => {
  ga.trackEvent({
    action: `blog_${action}`,
    category: 'blog_engagement',
    label: articleTitle || 'blog_listing',
    custom_parameters: {
      interaction_type: action,
      article_title: articleTitle,
    },
  });
};

/**
 * Track errors
 */
export const trackError = (errorType: string, errorMessage: string, page: string) => {
  ga.trackEvent({
    action: 'error_occurred',
    category: 'error',
    label: errorType,
    custom_parameters: {
      error_type: errorType,
      error_message: errorMessage,
      page_location: page,
    },
  });
};

// ================================================================
// AUTOMATIC PAGE VIEW TRACKING
// ================================================================

/**
 * Track page views automatically for React Router
 */
export const trackPageView = (pathname: string, title?: string) => {
  ga.trackPageView({
    page_path: pathname,
    page_title: title,
  });
};

export default ga;
